import { observer } from 'mobx-react-lite';
import React from 'react';
import {
  FocusContext,
  doesFocusableExist,
  setFocus,
  useFocusable
} from '@noriginmedia/norigin-spatial-navigation';
import { FocusKeysSheet, getEpisodeCardFocusKey } from '../sheet-helpers';
import clsx from 'clsx';

import { IS_BROWSER } from '@smart-tv/utils/is-browser';

import styles from '../episode-selector.module.scss';
import { rootStore } from '../_stores/store-root';
import { EpisodeExtended } from '../types';
import EpisodeCard from './sheet-episode-card';
import HorizontalSlider from '@smart-tv/components/horizontal-slider/horizontal-slider';
import SheetEpisodeSelectorSkeleton from './sheet-episode-selector-skeleton';

// ─── Episode list ─────────────────────────────────────────────

// Renders cards in batches instead of all at once, growing as the sentinel at the end of
// the loaded episodes scrolls into view - the full episode array is already fetched (season
// tabs need to anchor into the complete list), so this only delays rendering, not fetching.
const BATCH_SIZE = 15;

interface Props {
  episodes: EpisodeExtended[];
  onFocus: (episode: EpisodeExtended) => void;
  onMouseEnterEpisode?: (episode: EpisodeExtended) => void;
  onMouseEnterList?: (episode: EpisodeExtended) => void;
  onMouseLeaveEpisode?: () => void;
  onEnterPress: () => void;
  onClick: () => void;
  upFocusKey?: string;
  isFutureEpisodes?: boolean;
  hasSeasonTabs?: boolean;
  isSelectorFocused?: boolean;
  isLoading?: boolean;
}

const SheetEpisodeSelectorList = observer(
  ({
    episodes,
    onFocus,
    onMouseEnterEpisode,
    onMouseEnterList,
    onMouseLeaveEpisode,
    onEnterPress,
    onClick,
    isFutureEpisodes = false,
    hasSeasonTabs = true,
    isSelectorFocused = false,
    isLoading = false,
    upFocusKey = FocusKeysSheet.EPISODE_SELECTOR_SEASON_TABS
  }: Props) => {

    const handleUpArrow = React.useCallback(() => {
      setFocus(upFocusKey);
    }, [upFocusKey]);

    const handleListArrowPress = React.useCallback(
      (direction: string) => {
        if (direction === 'left' && isFutureEpisodes) {
          // Keep focus in-place for Kommer snart when moving left.
          return false;
        }

        return true;
      },
      [isFutureEpisodes]
    );

    const { ref, focusKey, hasFocusedChild } = useFocusable({
      trackChildren: true,
      saveLastFocusedChild: true,
      focusKey: FocusKeysSheet.EPISODE_SELECTOR_LIST,
      isFocusBoundary: isFutureEpisodes,
      focusBoundaryDirections: isFutureEpisodes ? ['left'] : [],
      onArrowPress: handleListArrowPress
    });

    const { focusedEpisode, setFocusedEpisode } = rootStore.episodesStore;
    const pendingFocusKeyRef = React.useRef<string | undefined>(undefined);

    React.useEffect(() => {
      const firstEpisode = episodes[0];
      const hasSelectedEpisode = episodes.some(
        (episode) =>
          episode.episodeId === focusedEpisode.episodeId &&
          episode.isNextEventforSeries === focusedEpisode.isNextEventforSeries
      );

      if (!hasSelectedEpisode) {
        const sameEpisode = episodes.find(
          (episode) => episode.episodeId === focusedEpisode.episodeId
        );
        const nextFocusedEpisode = sameEpisode ?? firstEpisode;

        if (nextFocusedEpisode) {
          setFocusedEpisode(
            nextFocusedEpisode.episodeId,
            nextFocusedEpisode.isNextEventforSeries,
            nextFocusedEpisode.episodeTitle
          );

          if (hasFocusedChild) {
            pendingFocusKeyRef.current = getEpisodeCardFocusKey(
              nextFocusedEpisode.episodeId,
              nextFocusedEpisode.isNextEventforSeries
            );
          }
        }
      }

      if (pendingFocusKeyRef.current && doesFocusableExist(pendingFocusKeyRef.current)) {
        setFocus(pendingFocusKeyRef.current);
        pendingFocusKeyRef.current = undefined;
      }
    }, [
      episodes,
      focusedEpisode.episodeId,
      focusedEpisode.isNextEventforSeries,
      hasFocusedChild,
      isLoading,
      setFocusedEpisode
    ]);

    const getFirstVisibleEpisode = React.useCallback(() => {
      const listElement = ref.current as HTMLDivElement | null;
      if (!listElement) return undefined;

      const listBounds = listElement.getBoundingClientRect();
      const cardElements = Array.from(
        listElement.querySelectorAll('[data-episode-index]')
      ) as HTMLElement[];

      for (const card of cardElements) {
        const cardBounds = card.getBoundingClientRect();
        const isHorizontallyVisible =
          cardBounds.right > listBounds.left && cardBounds.left < listBounds.right;

        if (!isHorizontallyVisible) continue;

        const index = Number(card.dataset.episodeIndex);
        if (!Number.isNaN(index) && episodes[index]) {
          return episodes[index];
        }
      }

      return episodes[0];
    }, [episodes, ref]);

    const handleListMouseEnter = React.useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        if (!onMouseEnterList) return;

        const target = event.target as HTMLElement;
        const cardElement = target.closest<HTMLElement>('[data-episode-index]');

        // Let the card's own onMouseEnter handle activation to avoid duplicate updates.
        if (cardElement?.dataset.episodeIndex) {
          return;
        }

        const firstVisibleEpisode = getFirstVisibleEpisode();
        if (firstVisibleEpisode) {
          onMouseEnterList(firstVisibleEpisode);
        }
      },
      [getFirstVisibleEpisode, onMouseEnterList]
    );

    const seasonNavigationKey = `${rootStore.sheetStore.sheet.contentId}-${
      rootStore.seasonsStore.selectedSeasonId ?? 'all'
    }-${isFutureEpisodes ? 1 : 0}`;

    const navigationStateKey = React.useMemo(
      () =>
        episodes
          .map((episode) => `${episode.episodeId}-${episode.isNextEventforSeries ? 1 : 0}`)
          .join('|'),
      [episodes]
    );

    const [visibleCount, setVisibleCount] = React.useState(BATCH_SIZE);
    const sentinelRef = React.useRef<HTMLDivElement>(null);
    const needsScrollRestoreRef = React.useRef(false);
    const wasLoadingRef = React.useRef(isLoading);

    React.useEffect(() => {
      if (wasLoadingRef.current && !isLoading) {
        needsScrollRestoreRef.current = true;
      }
      wasLoadingRef.current = isLoading;
    }, [isLoading]);

    React.useEffect(() => {
      const listElement = ref.current as HTMLDivElement | null;

      const isSameSeasonAsLastMount =
        rootStore.episodesStore.lastEpisodeListNavigationKey === seasonNavigationKey;
      rootStore.episodesStore.setLastEpisodeListNavigationKey(seasonNavigationKey);

      if (!isSameSeasonAsLastMount) {
        needsScrollRestoreRef.current = false;
        setVisibleCount(BATCH_SIZE);
        listElement?.scrollTo({ left: 0, behavior: 'auto' });
        return;
      }

      needsScrollRestoreRef.current = true;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seasonNavigationKey, ref]);

    React.useEffect(() => {
      if (!needsScrollRestoreRef.current || isLoading) return;

      const listElement = ref.current as HTMLDivElement | null;
      if (!listElement) return;

      const focusedIndex = episodes.findIndex(
        (episode) =>
          episode.episodeId === focusedEpisode.episodeId &&
          episode.isNextEventforSeries === focusedEpisode.isNextEventforSeries
      );
      if (focusedIndex < 0) return;

      if (focusedIndex >= visibleCount) {
        setVisibleCount((current) => Math.max(current, focusedIndex + 1));
        return;
      }

      const cardElement = listElement.querySelector<HTMLElement>(
        `[data-episode-index="${focusedIndex}"]`
      );
      if (!cardElement) return;

      listElement.scrollLeft = Math.max(0, cardElement.offsetLeft - listElement.clientWidth / 3);
      needsScrollRestoreRef.current = false;
    }, [
      episodes,
      focusedEpisode.episodeId,
      focusedEpisode.isNextEventforSeries,
      visibleCount,
      isLoading,
      ref
    ]);

    React.useEffect(() => {
      const listElement = ref.current as HTMLDivElement | null;
      const sentinel = sentinelRef.current;
      if (!listElement || !sentinel) return;

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) {
            setVisibleCount((current) => Math.min(episodes.length, current + BATCH_SIZE));
          }
        },
        { root: listElement, rootMargin: '0px 600px 0px 0px' }
      );

      observer.observe(sentinel);
      return () => observer.disconnect();
    }, [ref, episodes.length, visibleCount, isLoading]);

    const visibleEpisodes = episodes.slice(0, visibleCount);

    if (isLoading) {
      return <SheetEpisodeSelectorSkeleton hasSeasonTabs={hasSeasonTabs} />;
    }

    if (!episodes || episodes.length === 0) return null;

    return (
      <FocusContext.Provider value={focusKey}>
        <HorizontalSlider
          listRef={ref as React.RefObject<HTMLDivElement>}
          navigationStateKey={`${navigationStateKey}|${visibleCount}`}
          prevLabel="Forrige episoder"
          nextLabel="Næste episoder"
          className={styles['episode-list-wrapper']}
          listClassName={clsx(styles['episode-list'], {
            [styles['episode-list--future-episodes']]: isFutureEpisodes,
            [styles['episode-list--offset-top']]:
              !hasSeasonTabs && (IS_BROWSER || isSelectorFocused)
          })}
          navClassName={styles['episode-list-navigation--inset']}
          onMouseEnter={onMouseEnterList ? handleListMouseEnter : undefined}
        >
          {visibleEpisodes.map((episode, index) => (
            <EpisodeCard
              key={episode.episodeId}
              episode={episode}
              episodeIndex={index}
              onFocus={onFocus}
              onMouseEnterEpisode={onMouseEnterEpisode}
              onMouseLeaveEpisode={onMouseLeaveEpisode}
              onEnterPress={
                isFutureEpisodes || !episode.hasProviderAccess ? undefined : onEnterPress
              }
              onUpArrow={handleUpArrow}
              onClick={isFutureEpisodes || !episode.hasProviderAccess ? undefined : onClick}
              isFutureEpisodes={isFutureEpisodes}
              isSelectorFocused={isSelectorFocused}
              listRef={ref}
            />
          ))}
          {visibleCount < episodes.length && (
            <div ref={sentinelRef} className={styles['episode-list-sentinel']} />
          )}
        </HorizontalSlider>
      </FocusContext.Provider>
    );
  }
);

SheetEpisodeSelectorList.displayName = 'SheetEpisodeSelectorList';

export default SheetEpisodeSelectorList;
